import AppUtilsService from './appUtilsService';

// API service for organization management
const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001/api';

class OrganizationService {
  /**
   * Get the current user's organization
   * Uses the organizationId from localStorage or fetches it from user profile
   * @param {string} userId - User ID (optional, for fallback)
   * @param {string} token - Auth token
   * @returns {Promise<Object|null>} Organization object or null
   */
  static async getCurrentUserOrganization(userId, token) {
    try {
      const organizationId = await AppUtilsService.getOrganizationId(userId, token);
      
      if (!organizationId) {
        return null;
      }

      return await this.getOrganizationById(organizationId, token);
    } catch (error) {
      console.error('Error fetching current user organization:', error);
      throw error;
    }
  }

  static async getOrganizationById(organizationId, token) {
    try {
      const response = await fetch(`${API_BASE_URL}/organizations/${organizationId}`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      return data.data;
    } catch (error) {
      console.error('Error fetching organization:', error);
      throw error;
    }
  }

  static async createOrganization(organizationData, token) {
    try {
      const response = await fetch(`${API_BASE_URL}/organizations`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(organizationData),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      return data.data;
    } catch (error) {
      console.error('Error creating organization:', error);
      throw error;
    }
  }

  static async updateOrganization(organizationId, organizationData, token) {
    try {
      const response = await fetch(`${API_BASE_URL}/organizations/${organizationId}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(organizationData),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      return data.data;
    } catch (error) {
      console.error('Error updating organization:', error);
      throw error;
    }
  }

  static async deleteOrganization(organizationId, token) {
    try {
      const response = await fetch(`${API_BASE_URL}/organizations/${organizationId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Error deleting organization:', error);
      throw error;
    }
  }

  static async searchOrganizations(query, token) {
    try {
      const response = await fetch(`${API_BASE_URL}/organizations/search?query=${encodeURIComponent(query)}`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      return data.data;
    } catch (error) {
      console.error('Error searching organizations:', error);
      throw error;
    }
  }

  static async getOrganizationStats(token) {
    try {
      const response = await fetch(`${API_BASE_URL}/organizations/stats`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      return data.data;
    } catch (error) {
      console.error('Error fetching organization stats:', error);
      throw error;
    }
  }

  /**
   * Get a signed URL for uploading a logo
   * @param {string} organizationId - Organization ID
   * @param {string} token - Authentication token
   * @param {string} contentType - MIME type of the file
   * @param {string} fileName - Name of the file
   * @returns {Promise<Object>} Object containing uploadUrl and key
   */
  static async getLogoUploadUrl(organizationId, token, contentType, fileName) {
    try {
      const response = await fetch(`${API_BASE_URL}/organizations/${organizationId}/logo-upload-url?contentType=${encodeURIComponent(contentType)}&fileName=${encodeURIComponent(fileName)}`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error('Error getting upload URL');
      }

      const data = await response.json();
      return data.data;
    } catch (error) {
      console.error('Error getting logo upload URL:', error);
      throw error;
    }
  }

  /**
   * Upload a logo file to S3
   * @param {string} uploadUrl - Signed upload URL
   * @param {File} file - File to upload
   * @returns {Promise<boolean>} True if successful
   */
  static async uploadLogo(uploadUrl, file) {
    try {
      const response = await fetch(uploadUrl, {
        method: 'PUT',
        headers: {
          'Content-Type': file.type
        },
        body: file
      });

      if (!response.ok) {
        throw new Error('Error uploading file to S3');
      }

      return true;
    } catch (error) {
      console.error('Error uploading logo:', error);
      throw error;
    }
  }

  /**
   * Get the organization logo
   * @param {string} organizationId - Organization ID
   * @param {string} token - Authentication token
   * @returns {Promise<Blob|null>} Logo blob or null
   */
  static async getLogo(organizationId, token) {
    try {
      const response = await fetch(`${API_BASE_URL}/organizations/${organizationId}/logo`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) {
        if (response.status === 404) return null;
        throw new Error('Error fetching logo');
      }

      return await response.blob();
    } catch (error) {
      console.error('Error fetching logo:', error);
      return null;
    }
  }
}

export default OrganizationService;
